import { emit } from './client.js';
import { getCurrentTraceId } from './trace.js';

const MAX_MSG = 500;
const MAX_STACK = 2000;
const trunc = (s, n) => (typeof s === 'string' ? s.slice(0, n) : '');

// Secret/PII note: error messages + stacks routinely embed URLs. A URL carrying a
// credential query/fragment param (?token=, signed-URL ?signature=, OAuth-implicit
// #access_token=) or a bare JWT would otherwise be shipped verbatim. The server PII
// firewall only matches email/phone shapes — NOT credentials — so this client-side
// scrub is the real backstop for tokens; residual PII still relies on that firewall.
// LIMITATION: only catches anchored (?/&/#)key=value params + JWT shapes — secrets in
// a path segment, a JSON body, or an opaque (non-JWT) Bearer string are not reached.
const SECRET_QS =
  /([?&#](?:access_token|refresh_token|id_token|api_key|apikey|token|bearer|auth|session|key|jwt|code|signature|sig|password|pwd|secret)=)[^&\s]+/gi;
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
function scrub(s) {
  return typeof s === 'string'
    ? s.replace(SECRET_QS, '$1[redacted]').replace(JWT_RE, '[redacted-jwt]')
    : '';
}

export function reportError(err, source = 'window', extraProps) {
  try {
    const message = trunc(scrub(err && (err.message || String(err))), MAX_MSG);
    const stack = trunc(scrub(err && err.stack), MAX_STACK);
    const props = { message, stack, source, ...extraProps };
    emit({ event_type: 'js_error', name: 'js_error', trace_id: getCurrentTraceId(), props });
  } catch {
    // Telemetry must never break the app. Silently swallow any error from emit or trace resolution.
  }
}

let registered = false;
let onError = null;
let onRejection = null;
let appRef = null;
let prevHandler = null;

/** Register global + Vue error capture. Idempotent (mirrors nav/interactions). */
export function registerErrorTracking(app) {
  if (registered) return;
  registered = true;
  onError = (e) => reportError(e.error || e.message, 'window.onerror');
  onRejection = (e) => reportError(e.reason, 'unhandledrejection');
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  if (app && app.config) {
    appRef = app;
    prevHandler = app.config.errorHandler;
    app.config.errorHandler = (err, instance, info) => {
      const component =
        instance?.$options?.name ||
        instance?.$?.type?.name ||
        instance?.$?.type?.__name ||
        'anonymous';
      reportError(err, 'vue', { component, hook: info });
      if (typeof prevHandler === 'function') prevHandler(err, instance, info);
    };
  }
}

/** Detach listeners + restore the prior Vue handler (HMR teardown / tests). */
export function stopErrorTracking() {
  if (onError) window.removeEventListener('error', onError);
  if (onRejection) window.removeEventListener('unhandledrejection', onRejection);
  if (appRef && appRef.config) appRef.config.errorHandler = prevHandler;
  registered = false;
  onError = null;
  onRejection = null;
  appRef = null;
  prevHandler = null;
}

export function _resetForTest() { stopErrorTracking(); }
